import { useEffect, useMemo } from 'react';
import { logError } from 'services/logging/logging';
import { useReceiverProcessSpecification } from 'services/repositories/relationships/relationships';
import { setInitialCanonical, setProcessSpecificationFields, useWebEDIStore } from 'stores/webedi/webedi';
import { ProcessSpecificationDTO } from 'support/types';

export const useProcessSpecification = ({
  relationshipId,
  sourceMessageId,
}: {
  relationshipId: string | undefined;
  sourceMessageId: string | undefined;
}): ProcessSpecificationDTO | undefined => {
  const processSpecificationFields = useWebEDIStore((state) => state.processSpecificationFields);
  const { data: receiverProcessSpecification } = useReceiverProcessSpecification({
    variables: { relationshipId, sourceMessageId },
  });

  const finalReceiverProcessSpecification = useMemo(
    () =>
      receiverProcessSpecification
        ? {
            ...receiverProcessSpecification,
            fields: processSpecificationFields ?? receiverProcessSpecification?.fields,
            data: receiverProcessSpecification?.data,
            process: receiverProcessSpecification?.process,
          }
        : undefined,
    [processSpecificationFields, receiverProcessSpecification],
  );

  useEffect(() => {
    if (receiverProcessSpecification) {
      setProcessSpecificationFields(receiverProcessSpecification.fields);

      if (!receiverProcessSpecification.data) {
        logError('No data found in process specification for receiver');
      }

      if (!useWebEDIStore.getState().initialCanonical) {
        // If initial canonical is already set it means someone already intialized it
        // Example: MissingMessageUI sets initial canonical from OCR upload
        // We don't want to override it in that case
        setInitialCanonical(receiverProcessSpecification.data || undefined);
      }
    }
  }, [receiverProcessSpecification]);

  return finalReceiverProcessSpecification;
};
